import * as THREE from 'three';

/** Three.js material texture slots shown in the material panel. */
export const MATERIAL_MAP_DEFINITIONS = [
  { key: 'map', label: 'Map' },
  { key: 'specularMap', label: 'Specular Map' },
  { key: 'alphaMap', label: 'Alpha Map' },
  { key: 'envMap', label: 'Env Map' },
  { key: 'lightMap', label: 'Light Map' },
  { key: 'aoMap', label: 'AO Map' },
  { key: 'normalMap', label: 'Normal Map' },
  { key: 'roughnessMap', label: 'Roughness Map' },
  { key: 'metalnessMap', label: 'Metalness Map' },
  { key: 'emissiveMap', label: 'Emissive Map' },
  { key: 'bumpMap', label: 'Bump Map' },
  { key: 'displacementMap', label: 'Displacement Map' },
];

/** Color maps must stay in sRGB; data maps stay linear — matches GLTFLoader. */
const SRGB_MATERIAL_MAP_KEYS = new Set(['map', 'emissiveMap', 'specularMap']);

/**
 * Configure a freshly loaded texture so re-applying a downloaded map matches the
 * original GLB material (flipY + colorSpace + wrapping).
 */
export function configureMaterialMapTexture(texture, mapKey, referenceTexture = null) {
  if (!texture?.isTexture) return texture;

  const ref = referenceTexture?.isTexture ? referenceTexture : null;

  texture.flipY =
    typeof ref?.flipY === 'boolean' ? ref.flipY : false;

  if (ref?.colorSpace) {
    texture.colorSpace = ref.colorSpace;
  } else if (SRGB_MATERIAL_MAP_KEYS.has(mapKey)) {
    texture.colorSpace = THREE.SRGBColorSpace;
  } else {
    texture.colorSpace = THREE.LinearSRGBColorSpace;
  }

  if (ref) {
    if (ref.wrapS != null) texture.wrapS = ref.wrapS;
    if (ref.wrapT != null) texture.wrapT = ref.wrapT;
    if (ref.magFilter != null) texture.magFilter = ref.magFilter;
    if (ref.minFilter != null) texture.minFilter = ref.minFilter;
    if (ref.repeat) texture.repeat.copy(ref.repeat);
    if (ref.offset) texture.offset.copy(ref.offset);
    if (ref.center) texture.center.copy(ref.center);
    if (typeof ref.rotation === 'number') texture.rotation = ref.rotation;
    if (typeof ref.anisotropy === 'number') texture.anisotropy = ref.anisotropy;
  }

  texture.needsUpdate = true;
  return texture;
}

export const MAP_BACKUP_USERDATA_KEY = '_mapBackups';
export const MAP_VISIBILITY_USERDATA_KEY = '_mapVisibility';
export const MAP_PREVIEW_URLS_USERDATA_KEY = '_mapPreviewUrls';

/** Server/CDN URLs are safe to persist in the API; blob/data URLs are editor-only. */
export function isPublishableTextureUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) return false;
  return true;
}

/**
 * URLs that TextureLoader can re-apply after fetch.
 * data: is allowed (GLB extras embed); blob: is session-only and never restorable.
 */
export function isRestorableTextureUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith('blob:')) return false;
  return true;
}

/**
 * Map slots GLTFExporter actually writes into the file.
 * Everything else (alphaMap, specularMap, envMap, …) must be restored from
 * material extras / material_settings.maps after load.
 */
export const GLTF_NATIVE_MAP_KEYS = new Set([
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'emissiveMap',
]);

export function materialHasMapTexture(mat, mapKey) {
  if (!mat || !mapKey) return false;
  const backup = mat.userData?.[MAP_BACKUP_USERDATA_KEY]?.[mapKey];
  return !!(mat[mapKey]?.isTexture || backup?.isTexture);
}

export function objectHasEmbeddedGltfMaps(object) {
  return getMeshesFromObject(object).some((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    return materials.some((mat) =>
      MATERIAL_MAP_DEFINITIONS.some(({ key }) => mat?.[key]?.isTexture)
    );
  });
}

export function getMeshesFromObject(object) {
  if (!object) return [];
  const meshes = [];
  if (object.isMesh) meshes.push(object);
  object.traverse?.((child) => {
    if (child.isMesh) meshes.push(child);
  });
  return meshes;
}

function extensionFromMime(mime) {
  if (!mime) return 'png';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
  if (mime.includes('webp')) return 'webp';
  if (mime.includes('gif')) return 'gif';
  return 'png';
}

function extensionFromUrl(url) {
  if (typeof url !== 'string') return null;
  const clean = url.split('?')[0].split('#')[0];
  const match = clean.match(/\.(png|jpe?g|webp|gif)$/i);
  return match ? match[1].toLowerCase().replace('jpeg', 'jpg') : null;
}

async function fetchUrlAsBlob(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch texture: ${response.status}`);
  return response.blob();
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode texture'))),
      'image/png'
    );
  });
}

/**
 * Export the object's texture at native resolution / original file quality.
 * Prefers the original image bytes when available; otherwise encodes full-size PNG.
 */
export async function getFullQualityTextureBlob(texture) {
  if (!texture) return null;

  const img = texture.image || texture.source?.data || null;
  if (!img) return null;

  const tryUrl = async (url) => {
    if (!url || typeof url !== 'string') return null;
    // data: URLs that are already full uploads — keep original bytes
    if (url.startsWith('data:') || url.startsWith('blob:') || /^https?:/i.test(url)) {
      try {
        const blob = await fetchUrlAsBlob(url);
        return {
          blob,
          extension: extensionFromMime(blob.type) || extensionFromUrl(url) || 'png',
        };
      } catch {
        return null;
      }
    }
    return null;
  };

  if (typeof img === 'string') {
    const fromString = await tryUrl(img);
    if (fromString) return fromString;
  }

  const src = img.src || img.currentSrc || null;
  if (src) {
    const fromSrc = await tryUrl(src);
    if (fromSrc) return fromSrc;
  }

  const width = img.naturalWidth || img.width || 0;
  const height = img.naturalHeight || img.height || 0;
  if (!width || !height) return null;

  const canDraw =
    (typeof HTMLCanvasElement !== 'undefined' &&
      (img instanceof HTMLCanvasElement ||
        img instanceof HTMLImageElement ||
        (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) ||
        (typeof OffscreenCanvas !== 'undefined' &&
          img instanceof OffscreenCanvas))) ||
    (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap);

  if (!canDraw && !(img.width && img.height)) return null;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { colorSpace: 'srgb' }) || canvas.getContext('2d');
    if (!ctx) return null;
    // Keep pixel orientation identical to the GPU image (GLTF uses flipY=false).
    // Do not vertically flip here — re-apply configures flipY to match the original.
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await canvasToPngBlob(canvas);
    return { blob, extension: 'png' };
  } catch {
    return null;
  }
}

export async function downloadFullQualityMaterialMap(texture, label, fallbackUrl) {
  const safeName = String(label || 'material-map')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'material-map';

  let result = await getFullQualityTextureBlob(texture);

  if (!result && fallbackUrl) {
    try {
      const blob = await fetchUrlAsBlob(fallbackUrl);
      result = {
        blob,
        extension: extensionFromMime(blob.type) || extensionFromUrl(fallbackUrl) || 'png',
      };
    } catch {
      result = null;
    }
  }

  if (!result?.blob) return false;

  const objectUrl = URL.createObjectURL(result.blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${safeName}.${result.extension || 'png'}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
  return true;
}

/** Draw an image-like source to a small JPEG data URL for UI thumbnails. */
function encodePreviewDataUrl(img, width, height) {
  const MAX_PREVIEW_EDGE = 256;
  const w0 = width || img.width || img.naturalWidth || 0;
  const h0 = height || img.height || img.naturalHeight || 0;
  if (!w0 || !h0) return null;

  try {
    if (w0 <= MAX_PREVIEW_EDGE && h0 <= MAX_PREVIEW_EDGE) {
      if (typeof HTMLCanvasElement !== 'undefined' && img instanceof HTMLCanvasElement) {
        return img.toDataURL('image/jpeg', 0.7);
      }
    }
    const scale = MAX_PREVIEW_EDGE / Math.max(w0, h0);
    const w = Math.max(1, Math.round(w0 * Math.min(1, scale)));
    const h = Math.max(1, Math.round(h0 * Math.min(1, scale)));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.7);
  } catch {
    return null;
  }
}

/** Preview URL for UI thumbnails (data URL, http URL, or blob). */
export function getTexturePreviewUrl(texture) {
  if (!texture) return null;

  const img = texture.image || texture.source?.data || null;
  if (!img) return null;

  if (typeof img === 'string') return img;
  // Prefer durable http/cdn src; skip blob: so panel still falls through to encode.
  if (img.src && !String(img.src).startsWith('blob:')) return img.src;
  if (img.currentSrc && !String(img.currentSrc).startsWith('blob:')) {
    return img.currentSrc;
  }

  if (typeof HTMLCanvasElement !== 'undefined' && img instanceof HTMLCanvasElement) {
    return encodePreviewDataUrl(img, img.width, img.height);
  }

  if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) {
    return encodePreviewDataUrl(img, img.width, img.height);
  }

  // Embedded GLB images often decode as HTMLImageElement without a usable src.
  if (typeof HTMLImageElement !== 'undefined' && img instanceof HTMLImageElement) {
    if (img.complete && img.naturalWidth > 0) {
      return encodePreviewDataUrl(img, img.naturalWidth, img.naturalHeight);
    }
    if (img.src) return img.src;
  }

  // OffscreenCanvas / generic drawable with width/height.
  if (img.width && img.height && typeof img !== 'string') {
    return encodePreviewDataUrl(img, img.width, img.height);
  }

  return null;
}

/**
 * Encode a texture to a restorable data URL for GLB material extras.
 * Caps edge length so publish stays responsive for large bump/alpha maps.
 */
export async function getRestorableTextureDataUrl(texture, maxEdge = 1024) {
  if (!texture?.isTexture) return null;

  const img = texture.image || texture.source?.data || null;
  if (!img) return null;

  const w0 = img.naturalWidth || img.width || 0;
  const h0 = img.naturalHeight || img.height || 0;
  if (!w0 || !h0) {
    const preview = getTexturePreviewUrl(texture);
    return preview && String(preview).startsWith('data:') ? preview : null;
  }

  try {
    const scale = Math.min(1, maxEdge / Math.max(w0, h0));
    const w = Math.max(1, Math.round(w0 * scale));
    const h = Math.max(1, Math.round(h0 * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { colorSpace: 'srgb' }) || canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/png');
  } catch {
    const preview = getTexturePreviewUrl(texture);
    return preview && String(preview).startsWith('data:') ? preview : null;
  }
}

export function ensureMapUserData(mat) {
  if (!mat) return null;
  mat.userData = mat.userData || {};
  if (!mat.userData[MAP_BACKUP_USERDATA_KEY]) {
    mat.userData[MAP_BACKUP_USERDATA_KEY] = {};
  }
  if (!mat.userData[MAP_VISIBILITY_USERDATA_KEY]) {
    mat.userData[MAP_VISIBILITY_USERDATA_KEY] = {};
  }
  if (!mat.userData[MAP_PREVIEW_URLS_USERDATA_KEY]) {
    mat.userData[MAP_PREVIEW_URLS_USERDATA_KEY] = {};
  }
  return mat.userData;
}

export function setMapPreviewUrl(mat, mapKey, url) {
  if (!mat || !mapKey || !url) return;
  const ud = ensureMapUserData(mat);
  ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
}

/**
 * Prefer a durable URL for undo/redo (stored preview / image src).
 * Avoids relying only on compressed thumbnail data URLs.
 */
export function getMaterialMapUndoUrl(material, mapKey) {
  if (!material || !mapKey) return null;
  const stored =
    material.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY]?.[mapKey] || null;
  if (typeof stored === 'string' && stored.trim()) return stored;

  const resolved = resolveMapOnMaterial(material, mapKey);
  if (typeof resolved.previewUrl === 'string' && resolved.previewUrl.trim()) {
    return resolved.previewUrl;
  }

  const tex = resolved.texture;
  const img = tex?.image || tex?.source?.data;
  if (!img) return null;
  if (typeof img === 'string') return img;
  if (img.src) return img.src;
  if (img.currentSrc) return img.currentSrc;
  return null;
}

/** Snapshot one map slot on a material for undo/redo. */
export function captureMaterialMapSlotState(material, mapKey) {
  if (!material || !mapKey) return null;
  const resolved = resolveMapOnMaterial(material, mapKey);
  const url = getMaterialMapUndoUrl(material, mapKey);
  if (url) setMapPreviewUrl(material, mapKey, url);
  return {
    materialUuid: material.uuid,
    mapKey,
    url,
    visibility: !!resolved.enabled,
    hasTexture: !!(resolved.texture || url),
  };
}

/** Snapshot a map slot across every material under an object. */
export function captureObjectMapSlotStates(object, mapKey) {
  const byUuid = new Map();
  getMeshesFromObject(object).forEach((mesh) => {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((mat) => {
      if (!mat?.uuid || byUuid.has(mat.uuid)) return;
      byUuid.set(mat.uuid, {
        material: mat,
        state: captureMaterialMapSlotState(mat, mapKey),
      });
    });
  });
  return Array.from(byUuid.values());
}

/** Restore one map slot from an undo snapshot. */
export function applyMaterialMapSlotState(material, mapKey, state) {
  if (!material || !mapKey || !state) return Promise.resolve();

  const ud = ensureMapUserData(material);

  if (!state.hasTexture || !state.url) {
    const old = material[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey];
    if (old?.isTexture) {
      ud[MAP_BACKUP_USERDATA_KEY][mapKey] = old;
    }
    ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = false;
    material[mapKey] = null;
    delete ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey];
    material.needsUpdate = true;
    return Promise.resolve();
  }

  setMapPreviewUrl(material, mapKey, state.url);

  return new Promise((resolve) => {
    const loader = new THREE.TextureLoader();
    loader.load(
      state.url,
      (texture) => {
        const old = material[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey];
        configureMaterialMapTexture(texture, mapKey, old);
        ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
        ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = state.visibility !== false;
        material[mapKey] = state.visibility === false ? null : texture;
        material.needsUpdate = true;
        resolve();
      },
      undefined,
      () => resolve()
    );
  });
}

export function applyObjectMapSlotStates(entries, mapKey) {
  if (!entries?.length || !mapKey) return Promise.resolve();
  return Promise.all(
    entries.map(({ material, state }) =>
      applyMaterialMapSlotState(material, mapKey, state)
    )
  );
}

const MATERIAL_LIBRARY_UNDO_KEY = 'materialLibrary';

/** Capture mesh ↔ material bindings + unused library for list undo. */
export function captureMeshMaterialBindings(root) {
  if (!root) return { bindings: [], library: [], materialNames: {} };
  const bindings = [];
  const materialNames = {};

  const visit = (mesh) => {
    if (!mesh?.isMesh && !mesh?.isSkinnedMesh) return;
    const mats = Array.isArray(mesh.material)
      ? [...mesh.material]
      : [mesh.material];
    bindings.push({
      mesh,
      materials: mats.filter(Boolean),
    });
    mats.forEach((mat) => {
      if (mat?.uuid) materialNames[mat.uuid] = mat.name || '';
    });
  };

  if (root.isMesh || root.isSkinnedMesh) visit(root);
  root.traverse?.(visit);

  const library = Array.isArray(root.userData?.[MATERIAL_LIBRARY_UNDO_KEY])
    ? [...root.userData[MATERIAL_LIBRARY_UNDO_KEY]]
    : [];
  library.forEach((mat) => {
    if (mat?.uuid) materialNames[mat.uuid] = mat.name || '';
  });

  return {
    bindings,
    library,
    materialNames,
    libraryKey: MATERIAL_LIBRARY_UNDO_KEY,
  };
}

export function restoreMeshMaterialBindings(root, snapshot) {
  if (!root || !snapshot) return;
  snapshot.bindings?.forEach(({ mesh, materials }) => {
    if (!mesh) return;
    const list = (materials || []).filter(Boolean);
    mesh.material = list.length > 1 ? list : list[0] || mesh.material;
    mesh.userData = mesh.userData || {};
    mesh.userData.threeMaterialEdited = true;
  });

  const libraryKey = snapshot.libraryKey || MATERIAL_LIBRARY_UNDO_KEY;
  root.userData = root.userData || {};
  root.userData[libraryKey] = Array.isArray(snapshot.library)
    ? [...snapshot.library]
    : [];
  root.userData.threeMaterialEdited = true;

  if (snapshot.materialNames) {
    const applyName = (mat) => {
      if (!mat?.uuid) return;
      if (snapshot.materialNames[mat.uuid] !== undefined) {
        mat.name = snapshot.materialNames[mat.uuid];
      }
    };
    snapshot.bindings?.forEach(({ materials }) => materials?.forEach(applyName));
    snapshot.library?.forEach(applyName);
  }
}

export function getMapVisibility(mat, mapKey) {
  if (!mat) return false;
  const explicit = mat.userData?.[MAP_VISIBILITY_USERDATA_KEY]?.[mapKey];
  if (typeof explicit === 'boolean') return explicit;
  return !!mat[mapKey];
}

export function resolveMapOnMaterial(mat, mapKey) {
  if (!mat) return { texture: null, enabled: false };

  const backups = mat.userData?.[MAP_BACKUP_USERDATA_KEY] || {};
  const previewUrls = mat.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY] || {};
  const texture = mat[mapKey] || backups[mapKey] || null;
  const enabled = texture ? getMapVisibility(mat, mapKey) : false;

  return {
    texture,
    enabled,
    previewUrl: previewUrls[mapKey] || (texture ? getTexturePreviewUrl(texture) : null),
  };
}

/**
 * Collect map state from the selected object (first mesh with each map wins).
 */
export function collectMaterialMapList(object) {
  const meshes = getMeshesFromObject(object);

  return MATERIAL_MAP_DEFINITIONS.map(({ key, label }) => {
    let texture = null;
    let enabled = false;
    let previewUrl = null;
    let aoMapIntensity = 1;

    for (const mesh of meshes) {
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];

      for (const mat of materials) {
        if (!mat) continue;
        const resolved = resolveMapOnMaterial(mat, key);
        if (resolved.texture || resolved.previewUrl) {
          texture = resolved.texture;
          enabled = resolved.enabled;
          previewUrl = resolved.previewUrl;
          if (key === 'aoMap' && mat.aoMapIntensity != null) {
            aoMapIntensity = mat.aoMapIntensity;
          }
          break;
        }
      }
      if (texture || previewUrl) break;
    }

    return {
      key,
      label,
      texture,
      previewUrl,
      hasTexture: !!(texture || previewUrl),
      enabled: !!(texture || previewUrl) && enabled,
      aoMapIntensity,
    };
  });
}

/** Toggle map on mesh (texture stays in backup; boolean controls slot binding). */
export function setMaterialMapVisibility(object, mapKey, visible) {
  const meshes = getMeshesFromObject(object);

  meshes.forEach((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];

    materials.forEach((mat) => {
      if (!mat) return;

      const ud = ensureMapUserData(mat);
      const backups = ud[MAP_BACKUP_USERDATA_KEY];
      const texture = mat[mapKey] || backups[mapKey];
      if (!texture) return;

      backups[mapKey] = texture;
      const preview = getTexturePreviewUrl(texture);
      if (preview) {
        ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = preview;
      }

      ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = visible;
      mat[mapKey] = visible ? texture : null;

      // alphaMap is ignored unless the material is transparent.
      if (mapKey === 'alphaMap' && visible) {
        enableMaterialTransparency(mat);
      }

      mat.needsUpdate = true;
    });
  });
}

/** Force Three.js blending so alphaMap / fades actually render. */
export function enableMaterialTransparency(mat) {
  if (!mat || mat.transparent === undefined) return;
  mat.transparent = true;
  if (mat.depthWrite !== undefined) mat.depthWrite = false;
  mat.userData = mat.userData || {};
  mat.userData.editorNeedsTransparency = true;
  if (mat.userData.originalMaterialProps) {
    mat.userData.originalMaterialProps.transparent = true;
    mat.userData.originalMaterialProps.depthWrite = false;
  }
  mat.needsUpdate = true;
}

/** When any mesh has a visible alphaMap, force transparent on those materials. */
export function ensureTransparentForActiveAlphaMap(object) {
  if (!object) return false;
  let enabled = false;
  getMeshesFromObject(object).forEach((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    materials.forEach((mat) => {
      if (!mat) return;
      const { texture, enabled: on } = resolveMapOnMaterial(mat, 'alphaMap');
      if (texture && on) {
        enableMaterialTransparency(mat);
        enabled = true;
      }
    });
  });
  return enabled;
}

/** True when the same texture instance is bound to another map slot or backup. */
export function isTextureReferencedOnMaterial(material, texture, excludeMapKey) {
  if (!material || !texture?.isTexture) return false;

  for (const { key } of MATERIAL_MAP_DEFINITIONS) {
    if (key === excludeMapKey) continue;
    if (material[key] === texture) return true;
    const backup = material.userData?.[MAP_BACKUP_USERDATA_KEY]?.[key];
    if (backup === texture) return true;
  }

  return false;
}

/** Populate map backups / preview URLs from live material slots (after GLB load). */
export function syncMaterialMapBackupsFromSlots(object) {
  getMeshesFromObject(object).forEach((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];

    materials.forEach((mat) => {
      if (!mat) return;
      const ud = ensureMapUserData(mat);

      MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
        const texture = mat[key];
        if (!texture?.isTexture) return;

        ud[MAP_BACKUP_USERDATA_KEY][key] = texture;
        if (typeof ud[MAP_VISIBILITY_USERDATA_KEY][key] !== 'boolean') {
          ud[MAP_VISIBILITY_USERDATA_KEY][key] = true;
        }
        const preview = getTexturePreviewUrl(texture);
        if (preview) {
          ud[MAP_PREVIEW_URLS_USERDATA_KEY][key] = preview;
        }
      });
    });
  });
}

/**
 * Restore map textures from stored settings (maps / mapPreviewUrls / legacy fields).
 * Skips URL loads when the slot already has a texture from GLB.
 */
export function restoreMaterialMapsFromSettings(object, settings = {}) {
  if (!object || !settings || typeof settings !== 'object') {
    return Promise.resolve();
  }

  const maps = settings.maps || {};
  const mapPreviewUrls = settings.mapPreviewUrls || {};
  const mapVisibility = settings.mapVisibility || {};
  const urlsByKey = new Map();

  MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
    const url = maps[key] || mapPreviewUrls[key];
    if (isRestorableTextureUrl(url)) {
      urlsByKey.set(key, url);
    }
  });

  if (isRestorableTextureUrl(settings.textureImage)) {
    urlsByKey.set('emissiveMap', settings.textureImage);
  }
  if (isRestorableTextureUrl(settings.NormalMap)) {
    urlsByKey.set('normalMap', settings.NormalMap);
  }
  if (isRestorableTextureUrl(settings.metallic?.map)) {
    urlsByKey.set('metalnessMap', settings.metallic.map);
  }

  if (!urlsByKey.size) {
    return Promise.resolve();
  }

  const loader = new THREE.TextureLoader();
  if (typeof loader.setCrossOrigin === 'function') {
    loader.setCrossOrigin('anonymous');
  }

  const meshes = getMeshesFromObject(object);
  const tasks = [];

  urlsByKey.forEach((url, mapKey) => {
    if (!isRestorableTextureUrl(url)) return;

    tasks.push(
      new Promise((resolve) => {
        const syncVisibilityOnExisting = () => {
          meshes.forEach((mesh) => {
            const materials = Array.isArray(mesh.material)
              ? mesh.material
              : [mesh.material];
            materials.forEach((mat) => {
              if (!mat || !materialHasMapTexture(mat, mapKey)) return;
              const ud = ensureMapUserData(mat);
              const texture =
                mat[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey];
              if (!texture?.isTexture) return;
              ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
              if (isRestorableTextureUrl(url)) {
                ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
              }
              if (typeof mapVisibility[mapKey] === 'boolean') {
                ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = mapVisibility[mapKey];
                mat[mapKey] = mapVisibility[mapKey] ? texture : null;
              }
              mat.needsUpdate = true;
            });
          });
        };

        const materialsNeedingUrl = [];
        meshes.forEach((mesh) => {
          const materials = Array.isArray(mesh.material)
            ? mesh.material
            : [mesh.material];
          materials.forEach((mat) => {
            if (!mat || materialHasMapTexture(mat, mapKey)) return;
            materialsNeedingUrl.push(mat);
          });
        });

        if (!materialsNeedingUrl.length) {
          syncVisibilityOnExisting();
          resolve();
          return;
        }

        loader.load(
          url,
          (texture) => {
            if (!texture?.isTexture) {
              resolve();
              return;
            }
            texture.needsUpdate = true;
            const visible =
              typeof mapVisibility[mapKey] === 'boolean'
                ? mapVisibility[mapKey]
                : true;

            materialsNeedingUrl.forEach((mat) => {
              const ud = ensureMapUserData(mat);
              const old =
                mat[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey] || null;
              configureMaterialMapTexture(texture, mapKey, old);
              ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
              ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
              ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = visible;
              mat[mapKey] = visible ? texture : null;
              // alphaMap only affects the surface when the material is transparent
              // (or uses alphaTest) — otherwise Three.js ignores it.
              if (mapKey === 'alphaMap' && visible) {
                enableMaterialTransparency(mat);
              }
              mat.needsUpdate = true;
            });
            syncVisibilityOnExisting();
            resolve();
          },
          undefined,
          () => resolve()
        );
      })
    );
  });

  return tasks.length ? Promise.all(tasks) : Promise.resolve();
}

export function syncMapVisibilitySettings(object) {
  const meshes = getMeshesFromObject(object);
  const mapVisibility = {};

  MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
    let visible = false;
    let hasMap = false;

    for (const mesh of meshes) {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        const { texture, enabled } = resolveMapOnMaterial(mat, key);
        if (texture || mat?.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY]?.[key]) {
          hasMap = true;
          visible = enabled;
          break;
        }
      }
      if (hasMap) break;
    }

    if (hasMap) {
      mapVisibility[key] = visible;
    }
  });

  return mapVisibility;
}

/** Snapshot visibility + preview URLs for GLB export extras (not API). */
export function collectMapExportExtrasFromObject(object) {
  const meshes = getMeshesFromObject(object);
  const mapVisibility = {};
  const mapPreviewUrls = {};

  for (const { key } of MATERIAL_MAP_DEFINITIONS) {
    let done = false;
    for (const mesh of meshes) {
      if (done) break;
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];

      for (const mat of materials) {
        if (!mat) continue;
        const resolved = resolveMapOnMaterial(mat, key);
        const preview =
          resolved.previewUrl ||
          mat.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY]?.[key];

        if (!resolved.texture && !preview) continue;

        mapVisibility[key] = getMapVisibility(mat, key);
        if (preview) mapPreviewUrls[key] = preview;
        done = true;
        break;
      }
    }
  }

  return { mapVisibility, mapPreviewUrls };
}

function meshSlotKey(mesh, root = null) {
  const parts = [];
  let node = mesh;
  while (node && node !== root) {
    parts.unshift(node.name || node.uuid);
    node = node.parent;
  }
  return parts.join('/');
}

function resolveMeshHierarchyRoot(object) {
  if (!object) return object;
  const isMeshWrapper = object.deleteType === 'mesh' || object.objType === '3d';
  if (
    isMeshWrapper &&
    Array.isArray(object.children) &&
    object.children.length === 1 &&
    object.children[0]?.traverse
  ) {
    return object.children[0];
  }
  return object;
}

function resolveMeshSlot(mesh, root, meshByKey, meshByName) {
  const relativeKey = meshSlotKey(mesh, root);
  if (relativeKey && meshByKey.has(relativeKey)) {
    return meshByKey.get(relativeKey);
  }
  const simpleName = mesh.name || mesh.uuid;
  if (simpleName && meshByName.has(simpleName)) {
    return meshByName.get(simpleName);
  }
  return null;
}

/** Copy map backups, visibility, preview URLs, and slot bindings between two materials. */
export function copyMaterialMapUserData(sourceMat, targetMat) {
  if (!sourceMat || !targetMat) return;

  const srcUd = ensureMapUserData(sourceMat);
  const tgtUd = ensureMapUserData(targetMat);

  tgtUd[MAP_VISIBILITY_USERDATA_KEY] = {
    ...(srcUd[MAP_VISIBILITY_USERDATA_KEY] || {}),
  };
  tgtUd[MAP_PREVIEW_URLS_USERDATA_KEY] = {
    ...(srcUd[MAP_PREVIEW_URLS_USERDATA_KEY] || {}),
  };
  tgtUd[MAP_BACKUP_USERDATA_KEY] = {};

  MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
    const srcBackups = srcUd[MAP_BACKUP_USERDATA_KEY] || {};
    const texture = srcBackups[key] || sourceMat[key] || null;
    if (!texture) return;

    tgtUd[MAP_BACKUP_USERDATA_KEY][key] = texture;
    const visible =
      typeof srcUd[MAP_VISIBILITY_USERDATA_KEY]?.[key] === 'boolean'
        ? srcUd[MAP_VISIBILITY_USERDATA_KEY][key]
        : !!sourceMat[key];
    tgtUd[MAP_VISIBILITY_USERDATA_KEY][key] = visible;
    targetMat[key] = visible ? texture : null;
  });

  if (sourceMat.userData?.originalMaterialProps) {
    targetMat.userData.originalMaterialProps = {
      ...sourceMat.userData.originalMaterialProps,
    };
  }

  targetMat.needsUpdate = true;
}

function copyMeshMaterialMaps(sourceMesh, targetMesh) {
  if (!sourceMesh?.material || !targetMesh?.material) return;

  const sourceMats = Array.isArray(sourceMesh.material)
    ? sourceMesh.material
    : [sourceMesh.material];
  const targetMats = Array.isArray(targetMesh.material)
    ? targetMesh.material
    : [targetMesh.material];

  sourceMats.forEach((srcMat, index) => {
    const tgtMat = targetMats[index] ?? targetMats[0];
    if (srcMat && tgtMat) {
      copyMaterialMapUserData(srcMat, tgtMat);
    }
  });

  if (sourceMesh.userData?.threeMaterialEdited) {
    targetMesh.userData = targetMesh.userData || {};
    targetMesh.userData.threeMaterialEdited = true;
  }
}

/** Copy per-mesh texture map state from source object onto duplicate target. */
export function copyMaterialMapsBetweenObjects(sourceObject, targetObject) {
  if (!sourceObject || !targetObject) return;

  const sourceRoot = resolveMeshHierarchyRoot(sourceObject);
  const targetRoot = resolveMeshHierarchyRoot(targetObject);
  const sourceMeshes = getMeshesFromObject(sourceRoot);
  const targetMeshes = getMeshesFromObject(targetRoot);
  if (!sourceMeshes.length || !targetMeshes.length) return;

  if (sourceMeshes.length === 1 && targetMeshes.length === 1) {
    copyMeshMaterialMaps(sourceMeshes[0], targetMeshes[0]);
    return;
  }

  const sourceByKey = new Map();
  const sourceByName = new Map();
  sourceMeshes.forEach((mesh) => {
    const relativeKey = meshSlotKey(mesh, sourceRoot);
    if (relativeKey) sourceByKey.set(relativeKey, mesh);
    const simpleName = mesh.name || mesh.uuid;
    if (simpleName && !sourceByName.has(simpleName)) {
      sourceByName.set(simpleName, mesh);
    }
  });

  targetMeshes.forEach((targetMesh) => {
    const sourceMesh =
      resolveMeshSlot(targetMesh, targetRoot, sourceByKey, sourceByName) ||
      (sourceMeshes.length === 1 ? sourceMeshes[0] : null);
    if (sourceMesh) {
      copyMeshMaterialMaps(sourceMesh, targetMesh);
    }
  });
}

/** After GLB load: restore hidden map images from material extras (not API). */
export function restoreMapsFromGltfUserData(object) {
  const meshes = getMeshesFromObject(object);
  const loader = new THREE.TextureLoader();
  if (typeof loader.setCrossOrigin === 'function') {
    loader.setCrossOrigin('anonymous');
  }

  const tasks = [];

  meshes.forEach((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];

    materials.forEach((mat) => {
      if (!mat?.userData) return;

      const mapVisibility = mat.userData[MAP_VISIBILITY_USERDATA_KEY];
      const mapPreviewUrls = mat.userData[MAP_PREVIEW_URLS_USERDATA_KEY];
      if (!mapPreviewUrls || typeof mapPreviewUrls !== 'object') return;

      ensureMapUserData(mat);

      Object.entries(mapPreviewUrls).forEach(([mapKey, url]) => {
        if (!isRestorableTextureUrl(url)) return;
        if (materialHasMapTexture(mat, mapKey)) return;

        const visible =
          mapVisibility && typeof mapVisibility[mapKey] === 'boolean'
            ? mapVisibility[mapKey]
            : true;

        tasks.push(
          new Promise((resolve) => {
            loader.load(
              url,
              (texture) => {
                if (!texture?.isTexture) {
                  resolve();
                  return;
                }
                if (materialHasMapTexture(mat, mapKey)) {
                  resolve();
                  return;
                }
                const ud = ensureMapUserData(mat);
                const old =
                  mat[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey] || null;
                configureMaterialMapTexture(texture, mapKey, old);
                ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
                ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
                ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = visible;
                mat[mapKey] = visible ? texture : null;
                if (mapKey === 'alphaMap' && visible) {
                  enableMaterialTransparency(mat);
                }
                mat.needsUpdate = true;
                resolve();
              },
              undefined,
              () => resolve()
            );
          })
        );
      });
    });
  });

  return tasks.length ? Promise.all(tasks) : Promise.resolve();
}

/**
 * Encode map slots that GLTFExporter does not write so they round-trip via
 * material.userData extras (restored by restoreMapsFromGltfUserData).
 */
export async function embedNonGltfMapUrlsInMaterials(object) {
  if (!object) return;

  const tasks = [];

  getMeshesFromObject(object).forEach((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];

    materials.forEach((mat) => {
      if (!mat) return;
      const ud = ensureMapUserData(mat);
      const backups = ud[MAP_BACKUP_USERDATA_KEY] || {};
      const visibility = ud[MAP_VISIBILITY_USERDATA_KEY] || {};
      const previewUrls = ud[MAP_PREVIEW_URLS_USERDATA_KEY] || {};

      MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
        if (GLTF_NATIVE_MAP_KEYS.has(key)) return;
        if (visibility[key] === false) return;

        const texture = mat[key] || backups[key];
        if (!texture?.isTexture) return;

        // Prefer an already-durable CDN URL when the editor stored one.
        const existing = previewUrls[key];
        if (isPublishableTextureUrl(existing)) return;

        tasks.push(
          getRestorableTextureDataUrl(texture).then((dataUrl) => {
            if (!dataUrl) return;
            ud[MAP_PREVIEW_URLS_USERDATA_KEY][key] = dataUrl;
            if (typeof visibility[key] !== 'boolean') {
              ud[MAP_VISIBILITY_USERDATA_KEY][key] = !!mat[key];
            }
          })
        );
      });
    });
  });

  if (tasks.length) await Promise.all(tasks);
}
